import { useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, CalendarDays } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { EventCheckinPanel } from '@/components/events/event-checkin-panel'
import { EventPaymentPanel } from '@/components/events/event-payment-panel'
import { EventInvoicesPanel } from '@/components/events/event-invoices-panel'
import { WorkspacePortalProvider } from '@/contexts/workspace-portal-context'
import { jobsApi } from '@/services/jobs'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'

function EventSelector() {
  const [searchParams] = useSearchParams()
  const serviceInstanceId = getServiceInstanceIdFromSearch(searchParams.toString())

  const eventsQuery = useQuery({
    queryKey: ['events-ops-list', serviceInstanceId],
    queryFn: () => jobsApi.getEvents({ page_size: 20, status: 'open' }),
    staleTime: 30_000,
  })

  const events = eventsQuery.data?.results ?? []

  return (
    <div className="container mx-auto max-w-2xl space-y-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">Attendance &amp; Pay</h1>
        <p className="mt-1 text-sm text-muted-foreground">Select an event to manage attendance and payments.</p>
      </div>
      {eventsQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      ) : !events.length ? (
        <div className="flex h-32 items-center justify-center rounded-xl border border-dashed text-sm text-muted-foreground">
          No active events found
        </div>
      ) : (
        <div className="space-y-2">
          {events.map((event) => (
            <Link
              key={String(event.id)}
              to={withServiceInstanceId(`/events/operations?job_id=${encodeURIComponent(String(event.id))}`, serviceInstanceId)}
              className="block"
            >
              <Card className="cursor-pointer transition-colors hover:bg-muted/40">
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">{event.title}</p>
                      {event.scheduled_start ? (
                        <p className="text-xs text-muted-foreground">{new Date(event.scheduled_start).toLocaleDateString()}</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {event.status ? (
                      <Badge variant="outline" className="capitalize text-xs">{event.status}</Badge>
                    ) : null}
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

function EventOperationsContent() {
  const [searchParams] = useSearchParams()
  const jobId = searchParams.get('job_id') || searchParams.get('id')

  if (!jobId) {
    return <EventSelector />
  }

  return (
    <div className="container mx-auto max-w-4xl space-y-4 p-4">
      <h1 className="text-xl font-semibold">Attendance &amp; Pay</h1>
      <Tabs defaultValue="attendance">
        <TabsList>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="payment">Payment</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
        </TabsList>
        <TabsContent value="attendance" className="mt-4">
          <EventCheckinPanel jobId={jobId} />
        </TabsContent>
        <TabsContent value="payment" className="mt-4">
          <EventPaymentPanel jobId={jobId} />
        </TabsContent>
        <TabsContent value="invoices" className="mt-4">
          <EventInvoicesPanel jobId={jobId} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default function EventOperationsPage() {
  return (
    <WorkspacePortalProvider mode="event">
      <EventOperationsContent />
    </WorkspacePortalProvider>
  )
}
